import { Alert, Button, Input, Modal, Table } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  listRevenueCategories,
  readRevenueDay,
  saveRevenueDay,
} from 'src/utils/revenueCategoryApi'
import {
  describeRevenueCategoryMultiplier,
  formatRevenuePounds,
  previewRevenueLine,
  previewRevenueTotal,
} from 'src/utils/revenueLinePreview'
import {
  saleFigureLinesWouldReplace,
  uncategorisedRevenue,
} from 'src/utils/revenueSplit'

const RevenueDayEntry = ({
  venueId,
  onSaved,
  onBlocked,
  onDateChange,
  presentation = 'card',
}) => {
  const [categories, setCategories] = useState(null)
  const [entries, setEntries] = useState({})
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedSale, setSavedSale] = useState(null)
  const [loadedLines, setLoadedLines] = useState([])
  const [open, setOpen] = useState(false)

  const load = useCallback(() => {
    if (!venueId) return undefined
    let cancelled = false
    listRevenueCategories(venueId)
      .then(data => {
        if (!cancelled) setCategories(Array.isArray(data) ? data : [])
        return null
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })
    return () => {
      cancelled = true
    }
  }, [venueId])

  useEffect(() => {
    setCategories(null)
    setEntries({})
    setLoadedLines([])
    return load()
  }, [load])

  useEffect(() => {
    if (onDateChange) onDateChange(date)
  }, [date, onDateChange])

  useEffect(() => {
    if (!venueId || !date || categories == null) return undefined
    let cancelled = false
    readRevenueDay(venueId, date)
      .then(day => {
        if (cancelled) return null
        const lines = Array.isArray(day?.revenueLines) ? day.revenueLines : []
        const next = {}
        lines.forEach(line => {
          next[String(line.categoryId)] =
            line.entry == null ? '' : String(line.entry)
        })
        setEntries(next)
        setLoadedLines(lines)
        setSavedSale(
          day?.sale != null && Number(day.sale) !== 0 ? Number(day.sale) : null,
        )
        return null
      })
      .catch(() => {
        if (!cancelled) setError('That day could not be opened.')
      })
    return () => {
      cancelled = true
    }
  }, [venueId, date, categories])

  const activeCategories = useMemo(
    () => (categories || []).filter(category => category.active !== false),
    [categories],
  )

  const savedByCategory = useMemo(() => {
    const map = {}
    loadedLines.forEach(line => {
      map[String(line.categoryId)] = line
    })
    return map
  }, [loadedLines])

  const rows = useMemo(
    () =>
      activeCategories.map(category => {
        const saved = savedByCategory[String(category._id)]
        const entry = entries[category._id]
        const unchanged =
          saved && String(entry ?? '') === String(saved.entry ?? '')
        const preview = previewRevenueLine({
          entry,
          multiplier: unchanged ? saved.multiplier : category.multiplier,
        })
        return {
          ...category,
          multiplier: unchanged ? saved.multiplier : category.multiplier,
          mode: unchanged
            ? saved.mode
            : Number(category.multiplier) === 1
            ? 'amount'
            : 'quantity',
          preview,
        }
      }),
    [activeCategories, entries, savedByCategory],
  )

  const total = previewRevenueTotal(rows.map(row => row.preview))
  const replacedFigure = saleFigureLinesWouldReplace(savedSale, total)
  const gap = uncategorisedRevenue(
    savedSale,
    rows
      .filter(row => row.preview.calculated != null)
      .map(row => ({ calculated: row.preview.calculated })),
  )

  const writeDay = async () => {
    setSaving(true)
    setError('')
    try {
      const result = await saveRevenueDay(venueId, {
        date,
        entries: activeCategories.map(category => ({
          categoryId: category._id,
          entry: entries[category._id] ?? '',
        })),
      })
      setSavedSale(result?.sale ?? total)
      if (onSaved) onSaved()
    } catch (err) {
      const apiErrors = err?.response?.data?.errors
      setError(
        (Array.isArray(apiErrors) && apiErrors[0]) ||
          'That day could not be saved.',
      )
    } finally {
      setSaving(false)
    }
  }

  const saveDay = () => {
    if (onBlocked) {
      onBlocked()
      return
    }
    if (rows.some(row => row.preview.error)) {
      setError('Enter a number for each line.')
      return
    }
    if (replacedFigure) {
      Modal.confirm({
        title: 'Replace the existing sales figure?',
        content: `${dayjs(date).format(
          'dddd D MMMM',
        )} is saved as ${formatRevenuePounds(
          replacedFigure.saved,
        )}. These lines total ${formatRevenuePounds(
          replacedFigure.next,
        )}. Saving replaces the saved figure.`,
        okText: 'Replace figure',
        cancelText: 'Keep the existing figure',
        onOk: writeDay,
      })
      return
    }
    writeDay()
  }

  if (categories == null) {
    return presentation === 'modal' ? <p>Loading categories…</p> : null
  }
  if (activeCategories.length === 0) return null

  const columns = [
    { title: 'Category', dataIndex: 'name', key: 'name' },
    {
      title: 'Entry',
      key: 'mode',
      render: (_, record) => {
        const label = describeRevenueCategoryMultiplier(record.multiplier)
        return label === 'Amount' ? 'Amount' : `Quantity ${label}`
      },
    },
    {
      title: dayjs(date).format('ddd D MMM'),
      key: 'today',
      render: (_, record) => (
        <Input
          aria-label={
            Number(record.multiplier) === 1 || record.mode === 'amount'
              ? `${record.name} amount`
              : `${record.name} quantity`
          }
          value={entries[record._id] || ''}
          onChange={event =>
            setEntries(current => ({
              ...current,
              [record._id]: event.target.value,
            }))
          }
          style={{ width: 120 }}
        />
      ),
    },
    {
      title: 'Line total',
      key: 'line',
      render: (_, record) =>
        record.preview.error || formatRevenuePounds(record.preview.calculated),
    },
  ]

  const form = (
    <>
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <Input
          aria-label='Revenue day'
          type='date'
          value={date}
          onChange={event => setDate(event.target.value)}
          style={{ width: 180 }}
        />
        <Button type='primary' loading={saving} onClick={saveDay}>
          Save day
        </Button>
        {savedSale != null ? (
          <span>Saved {formatRevenuePounds(savedSale)}</span>
        ) : null}
      </div>
      {error ? <Alert type='error' message={error} showIcon /> : null}
      {replacedFigure ? (
        <Alert
          type='warning'
          showIcon
          style={{ marginBottom: 12 }}
          message={`${dayjs(date).format(
            'dddd D MMMM',
          )} is saved as ${formatRevenuePounds(
            replacedFigure.saved,
          )}. These lines total ${formatRevenuePounds(
            replacedFigure.next,
          )}. Saving replaces the saved figure.`}
        />
      ) : null}
      <Table
        rowKey={record => record._id}
        columns={columns}
        dataSource={rows}
        pagination={false}
        footer={() =>
          gap == null
            ? `Day total ${formatRevenuePounds(total)}`
            : `Day total ${formatRevenuePounds(total)} · ${formatRevenuePounds(
                gap,
              )} not in a category`
        }
      />
    </>
  )

  if (presentation === 'modal') {
    return form
  }

  return (
    <section
      style={{
        background: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: 12,
        padding: 24,
        margin: '0 0 24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
      aria-label='Revenue day entry'
    >
      <h2 style={{ fontSize: 18, marginBottom: open ? 8 : 0 }}>
        <button
          type='button'
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          {open ? '▾' : '▸'} Enter a day
        </button>
      </h2>
      {open ? form : null}
    </section>
  )
}

export default RevenueDayEntry
